import { motion } from "framer-motion";
import { HiArrowRight, HiCheckCircle, HiOutlineSparkles } from "react-icons/hi";
import SectionTitle from "../components/SectionTitle";
import AnimatedCounter from "../components/AnimatedCounter";
import { counters, founder, teamMembers, timeline } from "../data/content";
import { fadeInUp, stagger } from "../utils/motion";

function AboutSection() {
  return (
    <section id="about" className="relative py-24 sm:py-28">
      <div className="section-shell">
        <SectionTitle
          eyebrow="About"
          title="BeeVerge Is Built Around Innovation"
          subtitle="We are a future-focused IT company helping startups and enterprises transform ideas into robust software products."
        />

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        >
          {counters.map((item) => (
            <AnimatedCounter
              key={item.label}
              value={item.value}
              label={item.label}
              suffix={item.suffix}
            />
          ))}
        </motion.div>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {timeline.map((item, index) => (
            <motion.article
              key={item.title}
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: index * 0.08 }}
              className="glass rounded-xl p-6 shadow-glass"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-300">0{index + 1}</p>
              <h3 className="mt-3 text-xl font-semibold text-white">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-blue-100/80">{item.text}</p>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 grid items-center gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="glass rounded-2xl p-6 shadow-glass sm:p-8"
          >
            <p className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">
              <HiOutlineSparkles className="h-4 w-4" />
              Founder Story
            </p>
            <h3 className="mt-5 text-2xl font-bold leading-tight text-white sm:text-4xl">
              {founder.name}
            </h3>
            <p className="mt-2 text-base font-semibold text-cyan-200">{founder.role}</p>
            <p className="mt-5 text-base leading-8 text-blue-100/82">{founder.summary}</p>

            <div className="mt-6 space-y-3">
              {founder.details.map((detail) => (
                <div key={detail} className="flex gap-3 text-sm leading-6 text-blue-100/85">
                  <HiCheckCircle className="mt-1 h-5 w-5 shrink-0 text-cyan-300" />
                  <span>{detail}</span>
                </div>
              ))}
            </div>

            <motion.a
              href="#contact"
              whileHover={{ scale: 1.03, x: 4 }}
              whileTap={{ scale: 0.98 }}
              className="mt-8 inline-flex items-center gap-3 rounded-lg border border-cyan-200/30 bg-cyan-300/10 px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-cyan-100 transition hover:shadow-neon"
            >
              Contact Us
              <HiArrowRight className="h-5 w-5" />
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 42 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative grid gap-4 sm:min-h-[520px]"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="relative overflow-hidden rounded-2xl border border-cyan-200/20 shadow-glass sm:absolute sm:left-0 sm:top-0 sm:w-[72%]"
            >
              <img
                src={founder.image}
                alt={`${founder.name}, ${founder.role}`}
                className="h-80 w-full object-cover sm:h-[410px]"
              />
            </motion.div>

            <motion.div
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="glass relative rounded-2xl p-4 shadow-glass sm:absolute sm:bottom-0 sm:right-0 sm:w-[72%] sm:p-5"
            >
              <div className="grid grid-cols-2 gap-3">
                <img
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=700&q=80"
                  alt="BeeVerge team collaborating on product planning"
                  className="h-32 w-full rounded-xl object-cover sm:h-40"
                />
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=700&q=80"
                  alt="BeeVerge engineers working together"
                  className="h-32 w-full rounded-xl object-cover sm:h-40"
                />
              </div>
              <div className="mt-4 rounded-xl border border-white/10 bg-black/25 p-4">
                <p className="text-sm font-semibold text-white">Leadership with execution depth</p>
                <p className="mt-2 text-sm leading-6 text-blue-100/75">
                  Strategy, design, development, and launch support stay connected from the first
                  conversation to every product release.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="mt-20">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end"
          >
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">
                Team
              </p>
              <h3 className="text-2xl font-bold leading-tight text-white sm:text-3xl">Meet The Core Team</h3>
            </div>
            <p className="max-w-xl text-sm leading-6 text-blue-100/75">
              A focused group of strategists, designers, engineers, and delivery specialists
              building reliable products for growing companies.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
          >
            {teamMembers.map((member, index) => (
              <motion.article
                key={member.name}
                variants={fadeInUp}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ delay: index * 0.05 }}
                className="glass group overflow-hidden rounded-2xl shadow-glass"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={`${member.name}, ${member.role}`}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#070710] to-transparent" />
                </div>
                <div className="p-5">
                  <h4 className="text-xl font-semibold text-white">{member.name}</h4>
                  <p className="mt-1 text-sm font-semibold text-cyan-200">{member.role}</p>
                  <p className="mt-3 text-sm leading-6 text-blue-100/75">{member.detail}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AboutSection;
